import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Canvas,
  Point,
} from 'fabric'
import {
  MousePointer2,
  MapPin,
  Goal,
  Trash2,
  ChevronRight,
  Upload,
  Image as ImageIcon,
  Video,
  Pencil,
  MoveUpRight,
  LandPlot,
  Satellite,
  Crosshair,
  MapPinned,
} from 'lucide-react'
import type { StrategicPointData, ViewingAngle, SPTool, MediaItem } from '@/pages/StrategicPointsMap'
import { POINT_ICONS, getIconColor, pointIconLabel } from './pointIcons'
import {
  editorChoiceButton, editorIconButton, editorInput, editorLabel, editorMenu, editorMenuItem,
  editorMenuItemDanger, editorSurface,
} from '../strategy/editorUi'
import { useAuth, isOperator } from '@/contexts/AuthContext'
import MapTileLayer from '@/components/strategy/MapTileLayer'
import { fetchMaps, type MapInfo } from '@/lib/maps'
import { mapFilter, useMapViewMode } from '@/lib/mapView'
import { useLanguage } from '@/contexts/LanguageContext'
import { DRAW_SCALE } from '@/lib/drawScale'
import CalloutLayer from '@/components/callouts/CalloutLayer'
import {
  CALLOUT_COLOR, CALLOUT_NAME_MAX, calloutAt, type Callout, type CalloutVertex,
} from '@/lib/callouts'

const CANVAS_SIZE = 1080
// Zoom sınırları — StrategyEditor ile aynı mantık: taban "tüm harita sığar"
// ölçeğine bağlı, tavan tile piramidinin bittiği noktanın üstünde.
const MAX_ZOOM = 48
const MIN_ZOOM_FIT_FACTOR = 0.9

// Görüş açısı (arrow/polygon) çizgi kalınlığı — dünya biriminde, zoom ile ölçeklenir.
// Değer HLL haritasına göre seçilmişti; Wardogs ölçeğine DRAW_SCALE ile iner
// (bkz. lib/drawScale.ts). Ekranda ANGLE_MIN_PX'in altına inmez: harita
// uzaklaştırıldığında çizgi kaybolmasın.
const ANGLE_STROKE = 1.2 * DRAW_SCALE
const ANGLE_MIN_PX = 1.5

// ── Marker geometry constants ────────────────────────────────────────────────
const ICON_SIZE = 36
const TRI_W = 24
const TRI_H = 12
const TRI_TOP = ICON_SIZE + 2   // 38
const PIVOT_Y = TRI_TOP + TRI_H // 50

// Mevki çiziminde ilk köşeye bu kadar (ekran pikseli) yakın tıklama çokgeni kapatır.
const CALLOUT_CLOSE_PX = 14

interface Ping {
  id: string
  x: number
  y: number
}

interface Props {
  mapName: string
  points: StrategicPointData[]
  selectedPointId: number | null
  onSelectPoint: (id: number | null) => void
  onAddPoint: (point: StrategicPointData) => void
  onUpdatePoint: (id: number, updates: Partial<StrategicPointData>) => void
  onRemovePoint: (id: number) => void
  tool: SPTool
  onToolChange: (t: SPTool) => void
  callouts: Callout[]
  selectedCalloutId: number | null
  onSelectCallout: (id: number | null) => void
  onAddCallout: (c: Callout) => void
  onUpdateCallout: (c: Callout) => void
  onDeleteCallout: (id: number) => void
}


export default function StrategicPointEditor({
  mapName,
  points,
  selectedPointId,
  onSelectPoint,
  onAddPoint,
  onUpdatePoint,
  onRemovePoint,
  tool,
  onToolChange,
  callouts,
  selectedCalloutId,
  onSelectCallout,
  onAddCallout,
  onUpdateCallout,
  onDeleteCallout,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const fabricRef = useRef<Canvas | null>(null)
  const vptRef = useRef({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 })
  const fitScaleRef = useRef(1)

  const { user } = useAuth()
  const setTool = onToolChange

  const [pings, setPings] = useState<Ping[]>([])
  const [drawingAnglePoints, setDrawingAnglePoints] = useState<{ x: number; y: number }[]>([])
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null)
  const [contextMenu, setContextMenu] = useState<{ pointId: number; clientX: number; clientY: number } | null>(null)
  const [selectedIconType, setSelectedIconType] = useState('')
  const [rightPanelOpen, setRightPanelOpen] = useState(true)
  const [markerMousePos, setMarkerMousePos] = useState<{ x: number; y: number } | null>(null)
  const [uploading, setUploading] = useState(false)
  const [editingPointId, setEditingPointId] = useState<number | null>(null)
  const [editingDescription, setEditingDescription] = useState('')

  // ── Mevki durumu ──────────────────────────────────────────────────────────
  // Çizim üç evreden geçer: köşeler ekleniyor (`calloutDraft`) → çokgen kapandı,
  // ad bekleniyor (`pendingCallout`) → kaydedildi. Ad sorulmadan kayıt
  // açılmaz: adsız bir mevki, oyunda kimsenin söyleyemeyeceği bir yerdir.
  const [calloutDraft, setCalloutDraft] = useState<CalloutVertex[]>([])
  const [pendingCallout, setPendingCallout] = useState<CalloutVertex[] | null>(null)
  const [pendingName, setPendingName] = useState('')
  const [calloutNameDraft, setCalloutNameDraft] = useState('')
  const [calloutError, setCalloutError] = useState('')
  // Sürüklenen sınır: kaydedilene kadar yalnızca ekranda yaşar.
  const [dragOverride, setDragOverride] = useState<{ id: number; points: CalloutVertex[] } | null>(null)
  const dragRef = useRef<{ id: number; index: number } | null>(null)
  // Ref olayın içinde eşzamanlı güncellenir: son `pointermove`'un render'ı
  // `pointerup`'tan önce gelmemiş olabilir, kaydedilen hâl son hâl olmalı.
  const dragOverrideRef = useRef<{ id: number; points: CalloutVertex[] } | null>(null)
  const calloutsRef = useRef(callouts)
  const calloutDraftRef = useRef<CalloutVertex[]>([])
  const pendingCalloutRef = useRef<CalloutVertex[] | null>(null)
  useEffect(() => { calloutsRef.current = callouts }, [callouts])
  useEffect(() => { calloutDraftRef.current = calloutDraft }, [calloutDraft])
  useEffect(() => { pendingCalloutRef.current = pendingCallout }, [pendingCallout])

  const selectedCallout = callouts.find(c => c.id === selectedCalloutId) ?? null
  useEffect(() => {
    setCalloutNameDraft(selectedCallout?.name ?? '')
    setCalloutError('')
    if (selectedCalloutId !== null) setEditingPointId(null)
  }, [selectedCalloutId, selectedCallout?.name])

  // Auto-open right panel when selecting a point, entering edit mode, or switching to SP tool
  useEffect(() => {
    if (selectedPointId !== null || editingPointId !== null || tool === 'point' ||
        tool === 'callout' || selectedCalloutId !== null || pendingCallout !== null) {
      setRightPanelOpen(true)
    }
  }, [selectedPointId, editingPointId, tool, selectedCalloutId, pendingCallout])

  // Sync local editing fields when editing point changes
  useEffect(() => {
    if (editingPointId === null) return
    const p = points.find(pt => pt.id === editingPointId)
    if (p) {
      setEditingDescription(p.description)
    }
  }, [editingPointId])
  // vptTick increments on every viewport change to trigger re-render of DOM overlays
  const [, setVptTick] = useState(0)
  const [mapReady, setMapReady] = useState(false)
  const toolRef = useRef(tool)
  const selectedPointIdRef = useRef(selectedPointId)
  const pointsRef = useRef(points)
  const selectedIconTypeRef = useRef(selectedIconType)
  const isPanningRef = useRef(false)
  const hasPannedRef = useRef(false)
  const justPlacedRef = useRef(false)
  const justFinishedDrawingRef = useRef(false)
  const lastPanRef = useRef({ x: 0, y: 0 })
  const drawingAngleRef = useRef<{ pointId: number; type: 'polygon' | 'arrow' } | null>(null)
  const mouseDownTargetRef = useRef<EventTarget | null>(null)

  useEffect(() => { toolRef.current = tool }, [tool])


  // Reset icon selection when switching away from point tool; clear selection when switching to it
  useEffect(() => {
    if (tool !== 'point') {
      setSelectedIconType('')
      setMarkerMousePos(null)
    } else {
      setEditingPointId(null)
      onSelectPoint(null)
    }
    if (tool === 'callout') {
      setEditingPointId(null)
      onSelectPoint(null)
      onSelectCallout(null)
    } else {
      setCalloutDraft([])
    }
  }, [tool]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { selectedPointIdRef.current = selectedPointId }, [selectedPointId])
  useEffect(() => { pointsRef.current = points }, [points])
  useEffect(() => { selectedIconTypeRef.current = selectedIconType }, [selectedIconType])

  // ── Cursor: hide when SP tool active and icon selected (like marker tool) ──
  useEffect(() => {
    const fc = fabricRef.current
    if (!fc) return
    if (tool === 'point' && selectedIconType !== '') {
      fc.defaultCursor = 'none'
    } else if (tool === 'callout') {
      fc.defaultCursor = 'crosshair'
    } else {
      fc.defaultCursor = 'default'
    }
  }, [selectedIconType, tool])

  // ── Mouse tracking for SP placement preview ──────────────────────────────────
  useEffect(() => {
    const fc = fabricRef.current
    if (!fc) return
    if (tool !== 'point' || selectedIconType === '') {
      setMarkerMousePos(null)
      return
    }
    const handleMove = (opt: any) => {
      if (tool === 'point' && selectedIconType !== '') {
        const pointer = fc.getScenePoint(opt.e)
        setMarkerMousePos({ x: pointer.x, y: pointer.y })
      }
    }
    fc.on('mouse:move', handleMove)
    return () => { fc.off('mouse:move', handleMove) }
  }, [tool, selectedIconType])

  // ── Toolbar button helper ──────────────────────────────────────────────────
  const toolBtn = (t: SPTool, Icon: typeof MousePointer2, label: string) => (
    <button
      key={t}
      onClick={() => { setTool(t); if (t === tool) { onSelectPoint(null); setEditingPointId(null) } }}
      className={editorIconButton(tool === t)}
      title={label}
    >
      <Icon className="h-4 w-4" />
    </button>
  )

  // ── Update viewport transform and trigger re-render ────────────────────────
  const updateVpt = useCallback(() => {
    const fc = fabricRef.current
    if (!fc) return
    const vpt = fc.viewportTransform!
    vptRef.current = { a: vpt[0], b: vpt[1], c: vpt[2], d: vpt[3], e: vpt[4], f: vpt[5] }
    setVptTick(t => t + 1)
  }, [])

  // Ref so the canvas mouse:down listener (created once on mount) always calls the latest handler
  const handleAngleDrawClickRef = useRef<(x: number, y: number) => void>(() => {})
  const handleCalloutClickRef = useRef<(x: number, y: number) => void>(() => {})

  // ── Canvas initialization ─────────────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current) return

    const fc = new Canvas(canvasRef.current, {
      width: CANVAS_SIZE,
      height: CANVAS_SIZE,
      selection: false,
    })

    fabricRef.current = fc

    // ── Zoom (scroll) — animated ease-out ────────────────────────────────
    let zoomTarget = 1
    let zoomPoint = { x: 0, y: 0 }
    let zoomRafId: number | null = null

    const animateZoom = () => {
      const current = fc.getZoom()
      const diff = zoomTarget - current
      if (Math.abs(diff) < 0.001) {
        fc.zoomToPoint(new Point(zoomPoint.x, zoomPoint.y), zoomTarget)
        updateVpt()
        zoomRafId = null
        return
      }
      fc.zoomToPoint(new Point(zoomPoint.x, zoomPoint.y), current + diff * 0.08)
      updateVpt()
      zoomRafId = requestAnimationFrame(animateZoom)
    }

    fc.on('mouse:wheel', (opt: any) => {
      const clampedDelta = Math.sign(opt.e.deltaY) * Math.min(Math.abs(opt.e.deltaY), 100)
      zoomTarget *= 0.9965 ** clampedDelta
      zoomTarget = Math.min(Math.max(fitScaleRef.current * MIN_ZOOM_FIT_FACTOR, zoomTarget), MAX_ZOOM)
      zoomPoint = { x: opt.e.offsetX, y: opt.e.offsetY }
      if (zoomRafId === null) zoomRafId = requestAnimationFrame(animateZoom)
      opt.e.preventDefault()
      opt.e.stopPropagation()
    })

    // ── Pan (right drag, or left-drag in select/point mode) ─
    // Middle button is handled via native listeners below (browser intercepts button=1 for autoscroll)
    const handleMouseDown = (opt: any) => {
      const e = opt.e as MouseEvent
      hasPannedRef.current = false
      if (e.button === 2) {
        isPanningRef.current = true
        lastPanRef.current = { x: e.clientX, y: e.clientY }
        fc.selection = false
        fc.defaultCursor = 'grabbing'
        return
      }
      if (e.button === 0) {
        const t = toolRef.current
        // In point mode: pan when no icon selected (placement is a click, not drag)
        if (t === 'point' && selectedIconTypeRef.current === '') {
          isPanningRef.current = true
          lastPanRef.current = { x: e.clientX, y: e.clientY }
          fc.selection = false
          fc.defaultCursor = 'grabbing'
        } else if (t === 'select') {
          isPanningRef.current = true
          lastPanRef.current = { x: e.clientX, y: e.clientY }
          fc.selection = false
          fc.defaultCursor = 'grabbing'
        }
      }
    }
    const handleMouseMove = (opt: any) => {
      const e = opt.e as MouseEvent
      if (isPanningRef.current) {
        const dx = e.clientX - lastPanRef.current.x
        const dy = e.clientY - lastPanRef.current.y
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasPannedRef.current = true
        const vpt = fc.viewportTransform!
        vpt[4] += dx
        vpt[5] += dy
        lastPanRef.current = { x: e.clientX, y: e.clientY }
        fc.requestRenderAll()
        updateVpt()
      }
      // Track mouse position for arrow preview
      const currentTool = toolRef.current
      if (currentTool === 'angle_arrow' || currentTool === 'angle_polygon' || currentTool === 'callout') {
        const pointer = fc.getScenePoint(opt.e)
        setMousePos({ x: pointer.x, y: pointer.y })
      }
    }
    const handleMouseUp = () => {
      isPanningRef.current = false
      if (fabricRef.current) {
        // Restore cursor based on tool/selection state
        const t = toolRef.current
        const iconSelected = selectedIconTypeRef.current !== ''
        if (t === 'point' && iconSelected) {
          fabricRef.current.defaultCursor = 'none'
        } else if (t === 'callout') {
          fabricRef.current.defaultCursor = 'crosshair'
        } else {
          fabricRef.current.defaultCursor = 'default'
        }
      }
    }

    fc.on('mouse:down', handleMouseDown)
    fc.on('mouse:move', handleMouseMove)
    fc.on('mouse:up', handleMouseUp)

    // Prevent context menu on canvas
    canvasRef.current.addEventListener('contextmenu', (e) => e.preventDefault())

    // ── Left-click actions ────────────────────────────────────────────────
    fc.on('mouse:down', (opt: any) => {
      if ((opt.e as MouseEvent).button !== 0) return
      if (isPanningRef.current) return

      const currentTool = toolRef.current
      const pointer = fc.getScenePoint(opt.e)

      if (currentTool === 'point' && selectedIconTypeRef.current !== '') {
        createPoint(pointer.x, pointer.y)
      } else if (currentTool === 'ping') {
        sendPing(pointer.x, pointer.y)
      } else if (currentTool === 'angle_polygon' || currentTool === 'angle_arrow') {
        handleAngleDrawClickRef.current(pointer.x, pointer.y)
      } else if (currentTool === 'callout') {
        handleCalloutClickRef.current(pointer.x, pointer.y)
      }
    })

    // ── Middle-button pan (native — browser intercepts button=1 before Fabric) ──
    let middlePanActive = false
    let middlePanLastX = 0
    let middlePanLastY = 0

    const onNativeMouseDown = (e: MouseEvent) => {
      if (e.button !== 1) return
      e.preventDefault()
      middlePanActive = true
      middlePanLastX = e.clientX
      middlePanLastY = e.clientY
      hasPannedRef.current = false
      fc.defaultCursor = 'grabbing'
    }
    const onNativeMouseMove = (e: MouseEvent) => {
      if (!middlePanActive) return
      const dx = e.clientX - middlePanLastX
      const dy = e.clientY - middlePanLastY
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasPannedRef.current = true
      const vpt = fc.viewportTransform!
      vpt[4] += dx
      vpt[5] += dy
      middlePanLastX = e.clientX
      middlePanLastY = e.clientY
      fc.requestRenderAll()
      updateVpt()
    }
    const onNativeMouseUp = (e: MouseEvent) => {
      if (e.button !== 1 || !middlePanActive) return
      middlePanActive = false
      const t = toolRef.current
      fc.defaultCursor = (t === 'point' && selectedIconTypeRef.current !== '') ? 'none'
        : t === 'callout' ? 'crosshair' : 'default'
    }

    const container = containerRef.current
    if (container) container.addEventListener('mousedown', onNativeMouseDown, { capture: true })
    window.addEventListener('mousemove', onNativeMouseMove)
    window.addEventListener('mouseup', onNativeMouseUp)

    // ── Dokunma: tek parmak kaydırma, iki parmak pinch-zoom ──────────────────
    // Mobilde SP editörü de salt görüntülemedir (araç rayı gizli); dokunma her
    // zaman haritayı kaydırır/yakınlaştırır. StrategyEditor ile aynı mantık.
    let touchMode: 'none' | 'pan' | 'pinch' = 'none'
    let touchLastX = 0, touchLastY = 0
    let pinchStartDist = 1, pinchStartZoom = 1
    const touchDist = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
    const clampZoom = (z: number) =>
      Math.max(fitScaleRef.current * MIN_ZOOM_FIT_FACTOR, Math.min(MAX_ZOOM, z))
    const onTouchStart = (e: TouchEvent) => {
      const el = e.target as HTMLElement
      if (el.closest('[data-ui-panel]') || el.closest('button') || el.closest('input') || el.closest('label')) return
      if (e.touches.length === 1) {
        touchMode = 'pan'
        touchLastX = e.touches[0].clientX
        touchLastY = e.touches[0].clientY
      } else if (e.touches.length >= 2) {
        touchMode = 'pinch'
        pinchStartDist = touchDist(e.touches[0], e.touches[1]) || 1
        pinchStartZoom = fc.getZoom()
        e.preventDefault()
      }
    }
    const onTouchMove = (e: TouchEvent) => {
      if (touchMode === 'pan' && e.touches.length === 1) {
        const dx = e.touches[0].clientX - touchLastX
        const dy = e.touches[0].clientY - touchLastY
        const vpt = fc.viewportTransform!.slice() as [number, number, number, number, number, number]
        vpt[4] += dx
        vpt[5] += dy
        fc.setViewportTransform(vpt)
        touchLastX = e.touches[0].clientX
        touchLastY = e.touches[0].clientY
        updateVpt()
        e.preventDefault()
      } else if (touchMode === 'pinch' && e.touches.length >= 2 && container) {
        const dist = touchDist(e.touches[0], e.touches[1])
        const z = clampZoom(pinchStartZoom * (dist / pinchStartDist))
        const rect = container.getBoundingClientRect()
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top
        fc.zoomToPoint(new Point(midX, midY), z)
        zoomTarget = z  // tekerlekle aynı hedefte kal
        updateVpt()
        e.preventDefault()
      }
    }
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) { touchMode = 'none'; return }
      // Pinch'ten tek parmağa düşünce kaydırmayı yeni parmaktan sürdür.
      touchMode = 'pan'
      touchLastX = e.touches[0].clientX
      touchLastY = e.touches[0].clientY
    }
    if (container) {
      container.addEventListener('touchstart', onTouchStart, { passive: false })
      container.addEventListener('touchmove', onTouchMove, { passive: false })
      container.addEventListener('touchend', onTouchEnd)
      container.addEventListener('touchcancel', onTouchEnd)
    }

    return () => {
      if (zoomRafId !== null) cancelAnimationFrame(zoomRafId)
      if (container) container.removeEventListener('mousedown', onNativeMouseDown, { capture: true })
      window.removeEventListener('mousemove', onNativeMouseMove)
      window.removeEventListener('mouseup', onNativeMouseUp)
      if (container) {
        container.removeEventListener('touchstart', onTouchStart)
        container.removeEventListener('touchmove', onTouchMove)
        container.removeEventListener('touchend', onTouchEnd)
        container.removeEventListener('touchcancel', onTouchEnd)
      }
      fc.dispose()
      fabricRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Map metadata (tile pyramid config) ───────────────────────────────────
  // Zemin Fabric backgroundImage değil; canvas'ın altındaki MapTileLayer çiziyor.
  const [mapInfo, setMapInfo] = useState<MapInfo | null>(null)
  const [mapView, , toggleMapView] = useMapViewMode()
  const { T } = useLanguage()
  useEffect(() => {
    if (!mapName) { setMapInfo(null); setMapReady(true); return }
    let cancelled = false
    setMapReady(false)
    fetchMaps()
      .then(maps => { if (!cancelled) setMapInfo(maps.find(m => m.name === mapName) ?? null) })
      .catch(() => { if (!cancelled) setMapInfo(null) })
      .finally(() => { if (!cancelled) setMapReady(true) })
    return () => { cancelled = true }
  }, [mapName])

  // ── Viewing angle overlay rendering ───────────────────────────────────────
  useEffect(() => {
    const fc = fabricRef.current
    if (!fc) return

    // Remove existing viewing angle shapes
    fc.getObjects().filter(o => (o as any).__isViewingAngle).forEach(o => fc.remove(o))

    // Draw viewing angles for all points
    for (const point of points) {
      if (!point.viewing_angles) continue
      // Only show viewing angles for the selected SP
      if (point.id !== selectedPointId) continue
      // All viewing angle types are now rendered as DOM SVG overlays
    }
    fc.requestRenderAll()
  }, [points, selectedPointId])

  // ── Point creation ────────────────────────────────────────────────────────
  const createPoint = useCallback(async (x: number, y: number, iconType?: string) => {
    justPlacedRef.current = true  // consumed by the next DOM click event, not here
    try {
      const res = await fetch('/api/strategic-points', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ map_name: mapName, x, y, icon_type: iconType || selectedIconTypeRef.current }),
      })
      if (res.ok) {
        const point: StrategicPointData = await res.json()
        point.media = []
        onAddPoint(point)
        onSelectPoint(point.id)
        setEditingPointId(point.id)
        setTool('select')
        setMarkerMousePos(null)
      } else if (res.status === 403) {
        alert(T.strategicPoints.noPermission)
      }
    } catch { /* ignore */ }
  }, [mapName, onAddPoint, onSelectPoint, setTool])

  // ── Point deletion ───────────────────────────────────────────────────────
  const deletePoint = useCallback(async (id: number) => {
    try {
      await fetch(`/api/strategic-points/${id}`, { method: 'DELETE', credentials: 'include' })
      onRemovePoint(id)
      if (editingPointId === id) setEditingPointId(null)
    } catch { /* ignore */ }
  }, [onRemovePoint])

  // ── Viewing angle deletion ────────────────────────────────────────────────
  const deleteViewingAngle = useCallback(async (pointId: number, angleIdx: number) => {
    const point = pointsRef.current.find(p => p.id === pointId)
    if (!point) return
    const updatedAngles = (point.viewing_angles || []).filter((_, i) => i !== angleIdx)
    try {
      const res = await fetch(`/api/strategic-points/${pointId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ viewing_angle_json: JSON.stringify(updatedAngles) }),
      })
      if (res.ok) onUpdatePoint(pointId, { viewing_angles: updatedAngles })
    } catch { /* ignore */ }
  }, [onUpdatePoint])

  // ── Ping ──────────────────────────────────────────────────────────────────
  const sendPing = useCallback((x: number, y: number) => {
    const id = `ping-${Date.now()}-${Math.random()}`
    setPings(prev => [...prev, { id, x, y }])
    setTimeout(() => setPings(prev => prev.filter(p => p.id !== id)), 3000)
  }, [])

  // ── Viewing angle drawing ─────────────────────────────────────────────────
  const finishAngleDrawing = useCallback((pts: { x: number; y: number }[]) => {
    const info = drawingAngleRef.current
    if (!info || pts.length < 1) {
      drawingAngleRef.current = null
      setDrawingAnglePoints([])
      setMousePos(null)
      setTool('select')
      return
    }

    const point = pointsRef.current.find(p => p.id === info.pointId)
    if (!point) {
      drawingAngleRef.current = null
      setDrawingAnglePoints([])
      setMousePos(null)
      setTool('select')
      return
    }

    // Prepend the point's own position as the first vertex
    const allPoints = [{ x: point.x, y: point.y }, ...pts]
    const newAngle: ViewingAngle = {
      type: info.type,
      points: allPoints,
      color: getIconColor(point.icon_type),
      fill_opacity: 0.5,
    }

    const updatedAngles = [...(point.viewing_angles || []), newAngle]

    fetch(`/api/strategic-points/${info.pointId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ viewing_angle_json: JSON.stringify(updatedAngles) }),
    }).then(res => {
      if (res.ok) {
        onUpdatePoint(info.pointId, { viewing_angles: updatedAngles })
      }
    })

    justFinishedDrawingRef.current = true
    drawingAngleRef.current = null
    setDrawingAnglePoints([])
    setMousePos(null)
    setTool('select')
  }, [onUpdatePoint, setTool])

  const handleAngleDrawClick = useCallback((x: number, y: number) => {
    const info = drawingAngleRef.current
    if (!info) return

    // Polygon close: click near anchor point to finish
    if (info.type === 'polygon') {
      const anchorPt = pointsRef.current.find(p => p.id === info.pointId)
      if (anchorPt && drawingAnglePoints.length >= 2) {
        const dist = Math.hypot(x - anchorPt.x, y - anchorPt.y)
        const threshold = 45 / (vptRef.current.a || 1)
        if (dist < threshold) {
          finishAngleDrawing(drawingAnglePoints)
          return
        }
      }
    }

    setDrawingAnglePoints(prev => {
      const newPoints = [...prev, { x, y }]
      // Arrow: finish after 1 click (anchor auto-prepended)
      if (info.type === 'arrow' && newPoints.length >= 1) {
        finishAngleDrawing(newPoints)
        return []
      }
      return newPoints
    })
  }, [finishAngleDrawing, drawingAnglePoints])

  // Keep the ref in sync so the mount-only canvas listener always calls the latest version
  useEffect(() => { handleAngleDrawClickRef.current = handleAngleDrawClick }, [handleAngleDrawClick])

  // ── Listen for angle drawing events from PointDetailPanel ─────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const { pointId, type } = (e as CustomEvent).detail
      drawingAngleRef.current = { pointId, type }
      setDrawingAnglePoints([])
      setTool(type === 'polygon' ? 'angle_polygon' : 'angle_arrow')
      onSelectPoint(pointId)
    }
    window.addEventListener('sp-start-angle-drawing', handler)
    return () => window.removeEventListener('sp-start-angle-drawing', handler)
  }, [onSelectPoint, setTool])


  // ── Mevki çizimi ──────────────────────────────────────────────────────────
  const closeCalloutDraft = useCallback((pts: CalloutVertex[]) => {
    if (pts.length < 3) return
    setPendingCallout(pts)
    setPendingName('')
    setCalloutError('')
    setCalloutDraft([])
    setMousePos(null)
    setTool('select')
  }, [setTool])

  const handleCalloutClick = useCallback((x: number, y: number) => {
    if (pendingCalloutRef.current) return
    const draft = calloutDraftRef.current
    if (draft.length >= 3) {
      const first = draft[0]
      if (Math.hypot(x - first.x, y - first.y) < CALLOUT_CLOSE_PX / (vptRef.current.a || 1)) {
        // Kapatan tıklamanın `click` olayı "boş haritaya tıklandı" diye
        // okunmasın (bkz. boş tuval işleyicisi).
        justFinishedDrawingRef.current = true
        closeCalloutDraft(draft)
        return
      }
    }
    setCalloutDraft([...draft, { x, y }])
  }, [closeCalloutDraft])

  useEffect(() => { handleCalloutClickRef.current = handleCalloutClick }, [handleCalloutClick])

  const cancelPendingCallout = useCallback(() => {
    setPendingCallout(null)
    setPendingName('')
    setCalloutError('')
  }, [])

  const savePendingCallout = useCallback(async () => {
    const pts = pendingCalloutRef.current
    const name = pendingName.trim()
    if (!pts || !name) return
    try {
      const res = await fetch('/api/callouts', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ map_name: mapName, name, points: pts }),
      })
      if (!res.ok) { setCalloutError(T.callouts.saveFailed); return }
      const created: Callout = await res.json()
      onAddCallout(created)
      cancelPendingCallout()
      onSelectCallout(created.id)
    } catch {
      setCalloutError(T.callouts.saveFailed)
    }
  }, [pendingName, mapName, onAddCallout, onSelectCallout, cancelPendingCallout]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Ad ya da sınır: uç ikisini de kabul eder, yanıt satırın tamamıdır. */
  const putCallout = useCallback(async (id: number, body: { name?: string; points?: CalloutVertex[] }) => {
    try {
      const res = await fetch(`/api/callouts/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) { setCalloutError(T.callouts.saveFailed); return false }
      onUpdateCallout(await res.json())
      setCalloutError('')
      return true
    } catch {
      setCalloutError(T.callouts.saveFailed)
      return false
    }
  }, [onUpdateCallout]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveCalloutName = useCallback(() => {
    const c = calloutsRef.current.find(x => x.id === selectedCalloutId)
    const name = calloutNameDraft.trim()
    if (!c || !c.can_edit) return
    if (!name) { setCalloutNameDraft(c.name); return }
    if (name !== c.name) putCallout(c.id, { name })
  }, [selectedCalloutId, calloutNameDraft, putCallout])

  /**
   * Köşe sürükleme. Sürüklenen sınır `dragOverride`'da yaşar ve bırakınca tek
   * bir PUT ile yazılır; kare başına istek atılmaz. İstek patlarsa override
   * düşer ve mevki son kaydedilmiş hâline döner.
   */
  const startVertexDrag = useCallback((id: number, index: number, points: CalloutVertex[]) => {
    dragRef.current = { id, index }
    dragOverrideRef.current = { id, points }
    setDragOverride({ id, points })
  }, [])

  useEffect(() => {
    if (!dragOverride) return
    const toWorld = (e: PointerEvent) => {
      const rect = containerRef.current!.getBoundingClientRect()
      const v = vptRef.current
      return { x: (e.clientX - rect.left - v.e) / v.a, y: (e.clientY - rect.top - v.f) / v.a }
    }
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current
      if (!d) return
      const prev = dragOverrideRef.current
      if (!prev || prev.id !== d.id) return
      const w = toWorld(e)
      const next = { id: prev.id, points: prev.points.map((p, i) => i === d.index ? w : p) }
      dragOverrideRef.current = next
      setDragOverride(next)
    }
    const onUp = () => {
      dragRef.current = null
      const cur = dragOverrideRef.current
      if (!cur) return
      const original = calloutsRef.current.find(c => c.id === cur.id)
      const moved = !original || original.points.length !== cur.points.length ||
        original.points.some((p, i) => p.x !== cur.points[i].x || p.y !== cur.points[i].y)
      const clear = () => { dragOverrideRef.current = null; setDragOverride(null) }
      if (moved) putCallout(cur.id, { points: cur.points }).finally(clear)
      else clear()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp, { once: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [dragOverride !== null, putCallout]) // eslint-disable-line react-hooks/exhaustive-deps

  const removeVertex = useCallback((c: Callout, index: number) => {
    if (c.points.length <= 3) return
    putCallout(c.id, { points: c.points.filter((_, i) => i !== index) })
  }, [putCallout])

  // ── Resize ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let initialResize = true
    const resizeCanvas = () => {
      const fc = fabricRef.current
      if (!fc) return
      const { width, height } = container.getBoundingClientRect()
      fc.setDimensions({ width, height })
      const scale = Math.min(width / CANVAS_SIZE, height / CANVAS_SIZE)
      fitScaleRef.current = scale
      if (initialResize) {
        const offsetX = (width - scale * CANVAS_SIZE) / 2
        const offsetY = (height - scale * CANVAS_SIZE) / 2
        fc.setViewportTransform([scale, 0, 0, scale, offsetX, offsetY])
        initialResize = false
      }
      fc.requestRenderAll()
      updateVpt()
    }

    let resizeTimer: ReturnType<typeof setTimeout> | null = null
    const scheduleResize = () => {
      if (initialResize) { resizeCanvas(); return }
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(resizeCanvas, 350)
    }

    resizeCanvas()
    const obs = new ResizeObserver(scheduleResize)
    obs.observe(container)
    return () => {
      obs.disconnect()
      if (resizeTimer) clearTimeout(resizeTimer)
    }
  }, [updateVpt])

  // ── Escape key to cancel drawing, deselect, or exit edit mode ──────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // Mevki çizimi: Enter kapatır, Backspace son köşeyi geri alır.
      if (toolRef.current === 'callout') {
        const el = e.target as HTMLElement | null
        const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
        if (e.key === 'Enter' && !typing) {
          e.preventDefault()
          closeCalloutDraft(calloutDraftRef.current)
          return
        }
        if (e.key === 'Backspace' && !typing) {
          e.preventDefault()
          setCalloutDraft(d => d.slice(0, -1))
          return
        }
        if (e.key === 'Escape') {
          setCalloutDraft([])
          setMousePos(null)
          setTool('select')
          return
        }
      }
      if (e.key === 'Escape' && pendingCalloutRef.current) {
        cancelPendingCallout()
        return
      }
      if (e.key === 'Escape') {
        if (toolRef.current === 'angle_polygon' || toolRef.current === 'angle_arrow') {
          drawingAngleRef.current = null
          setDrawingAnglePoints([])
          setMousePos(null)
          setTool('select')
        } else if (editingPointId !== null) {
          setEditingPointId(null)
        } else {
          onSelectPoint(null)
          onSelectCallout(null)
        }
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onSelectPoint, onSelectCallout, setTool, editingPointId, closeCalloutDraft, cancelPendingCallout])

  // ── Click on empty canvas: deselect, close edit, switch to select ──────────
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleMouseDown = (e: MouseEvent) => {
      mouseDownTargetRef.current = e.target
    }

    const isUiElement = (el: HTMLElement | null) =>
      !!el && !!(el.closest('[data-ui-panel]') || el.closest('button') || el.closest('input') || el.closest('textarea') || el.closest('label') || el.closest('[data-sp-marker]'))

    const handleClick = (e: MouseEvent) => {
      // Only left-click
      if (e.button !== 0) return
      // Ignore clicks that are the end of a pan gesture
      if (hasPannedRef.current) { hasPannedRef.current = false; return }
      // Ignore clicks that triggered a point placement; consume the flag here
      if (justPlacedRef.current) { justPlacedRef.current = false; return }
      // Ignore the click that finalized a drawing (polygon close or arrow endpoint)
      if (justFinishedDrawingRef.current) { justFinishedDrawingRef.current = false; return }
      // Ignore if mousedown originated from a UI element (panel, button, input, marker…)
      if (isUiElement(mouseDownTargetRef.current as HTMLElement)) return
      // Ignore if mouseup landed on a UI element
      if (isUiElement(e.target as HTMLElement)) return
      // Don't deselect while drawing viewing angles — canvas clicks are consumed by the drawing handler
      if (toolRef.current === 'angle_arrow' || toolRef.current === 'angle_polygon' || toolRef.current === 'callout') return
      // Adı bekleyen bir mevki varken harita tıklaması seçimi değiştirmez.
      if (pendingCalloutRef.current) return

      const fc = fabricRef.current
      if (!fc) return

      // Check if click hit any fabric object — if so, don't deselect
      const target = fc.findTarget(e as any)
      if (target) return

      // Mevkiler tıklanabilir DOM değil (üstlerinden harita sürüklenebilsin
      // diye), seçim tıklamanın dünya koordinatından yapılır.
      if (toolRef.current === 'select') {
        const hit = calloutAt(fc.getScenePoint(e as any), calloutsRef.current)
        if (hit) {
          setEditingPointId(null)
          onSelectCallout(hit.id)
          return
        }
      }

      // Click on empty canvas: deselect and switch to select tool
      setEditingPointId(null)
      onSelectPoint(null)
      onSelectCallout(null)
      if (toolRef.current === 'point') {
        setTool('select')
      }
    }
    container.addEventListener('mousedown', handleMouseDown, true)
    container.addEventListener('click', handleClick, true)
    return () => {
      container.removeEventListener('mousedown', handleMouseDown, true)
      container.removeEventListener('click', handleClick, true)
    }
  }, [onSelectPoint, onSelectCallout, setTool])

  // ── Compute point marker positions ────────────────────────────────────────
  const vpt = vptRef.current

  // Get anchor point for drawing (for arrow preview & polygon close handle)
  const drawingAnchor = drawingAngleRef.current
    ? points.find(p => p.id === drawingAngleRef.current!.pointId)
    : null
  const anchorColor = drawingAnchor ? getIconColor(drawingAnchor.icon_type) : '#ef4444'

  // ── Render ────────────────────────────────────────────────────────────────
  const isDrawing = tool === 'angle_polygon' || tool === 'angle_arrow' || tool === 'callout'

  // Ekrandaki mevkiler: sürüklenen sınır kaydedilene kadar override'dan okunur,
  // adı bekleyen çokgen de aynı katmanda (geçici id ile) çizilir.
  const shownCallouts: Callout[] = callouts.map(c =>
    dragOverride && dragOverride.id === c.id ? { ...c, points: dragOverride.points } : c)
  if (pendingCallout) {
    shownCallouts.push({
      id: -1, map_name: mapName, name: pendingName.trim(), points: pendingCallout,
      created_by: '', created_by_name: '', created_by_avatar: '', created_at: 0, updated_at: 0, can_edit: true,
    })
  }
  const editableCallout = tool === 'select' && !pendingCallout && selectedCallout?.can_edit
    ? shownCallouts.find(c => c.id === selectedCallout.id) ?? null
    : null
  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-hidden relative"
      style={{ visibility: mapReady ? 'visible' : 'hidden' }}
    >

      {/* Canvas */}
      {/* Harita zemini — canvas'ın altında, aynı viewport transform'uyla */}
      <MapTileLayer
        tiles={mapInfo?.tiles ?? null}
        base={mapInfo?.base ?? null}
        vpt={vptRef.current}
        worldSize={CANVAS_SIZE}
        filter={mapFilter(mapInfo?.luma, mapView)}
      />

      <canvas ref={canvasRef} />

      {/* ── Mevkiler ─────────────────────────────────────────────────────── */}
      <CalloutLayer
        callouts={shownCallouts}
        vpt={vpt}
        selectedId={pendingCallout ? -1 : selectedCalloutId}
        zIndex={2}
      />

      {/* Seçili mevkinin sınır tutamaçları, yalnızca düzenleyebilene.
          Köşe sürüklenir; kenar ortasındaki tutamaç sürüklenince yeni köşe
          olur; köşeye çift tıklamak onu siler (en az üç köşe kalır). */}
      {editableCallout && (() => {
        const pts = editableCallout.points
        const toS = (p: CalloutVertex) => ({ x: p.x * vpt.a + vpt.e, y: p.y * vpt.a + vpt.f })
        const original = callouts.find(c => c.id === editableCallout.id)!
        return (
          <div className="pointer-events-none absolute inset-0" style={{ zIndex: 7 }}>
            {pts.map((p, i) => {
              const q = pts[(i + 1) % pts.length]
              const m = toS({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 })
              return (
                <div
                  key={`mid-${i}`}
                  data-sp-marker
                  className="pointer-events-auto absolute h-2.5 w-2.5 cursor-copy rounded-full border border-black/60"
                  style={{ left: m.x, top: m.y, transform: 'translate(-50%, -50%)', background: CALLOUT_COLOR, opacity: 0.7 }}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return
                    e.preventDefault()
                    const next = [...pts.slice(0, i + 1), { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, ...pts.slice(i + 1)]
                    startVertexDrag(editableCallout.id, i + 1, next)
                  }}
                />
              )
            })}
            {pts.map((p, i) => {
              const sp = toS(p)
              return (
                <div
                  key={`v-${i}`}
                  data-sp-marker
                  className="pointer-events-auto absolute h-3 w-3 cursor-move rounded-[2px] border-2 bg-white"
                  style={{ left: sp.x, top: sp.y, transform: 'translate(-50%, -50%)', borderColor: CALLOUT_COLOR, boxShadow: '0 0 3px rgba(0,0,0,0.8)' }}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return
                    e.preventDefault()
                    startVertexDrag(editableCallout.id, i, pts)
                  }}
                  onDoubleClick={(e) => { e.stopPropagation(); removeVertex(original, i) }}
                />
              )
            })}
          </div>
        )
      })()}

      {/* ── Mevki çizim önizlemesi ───────────────────────────────────────── */}
      {tool === 'callout' && calloutDraft.length > 0 && (() => {
        const toS = (p: CalloutVertex) => ({ x: p.x * vpt.a + vpt.e, y: p.y * vpt.a + vpt.f })
        const path = [...calloutDraft, ...(mousePos ? [mousePos] : [])].map(toS)
        const first = toS(calloutDraft[0])
        const canClose = calloutDraft.length >= 3
        return (
          <div className="pointer-events-none absolute inset-0" style={{ zIndex: 7 }}>
            <svg className="absolute inset-0 h-full w-full" style={{ overflow: 'visible' }}>
              {path.length >= 3 && (
                <polygon points={path.map(p => `${p.x},${p.y}`).join(' ')} fill={CALLOUT_COLOR} fillOpacity={0.1} stroke="none" />
              )}
              <polyline points={path.map(p => `${p.x},${p.y}`).join(' ')} fill="none"
                stroke="#000" strokeOpacity={0.55} strokeWidth={4} strokeLinejoin="round" />
              <polyline points={path.map(p => `${p.x},${p.y}`).join(' ')} fill="none"
                stroke={CALLOUT_COLOR} strokeWidth={1.75} strokeDasharray="6 4" strokeLinejoin="round" />
            </svg>
            {calloutDraft.map((p, i) => {
              const sp = toS(p)
              const isFirst = i === 0
              const size = isFirst && canClose ? 14 : 8
              return (
                <div
                  key={i}
                  className="absolute rounded-[2px] border-2"
                  style={{
                    left: sp.x, top: sp.y, width: size, height: size,
                    transform: 'translate(-50%, -50%)',
                    borderColor: CALLOUT_COLOR,
                    background: isFirst ? '#fff' : CALLOUT_COLOR,
                    boxShadow: isFirst && canClose ? `0 0 8px ${CALLOUT_COLOR}` : '0 0 3px rgba(0,0,0,0.8)',
                    transition: 'width 120ms, height 120ms',
                  }}
                />
              )
            })}
            {/* Kapanış çizgisi: ilk köşeye bağlanacağını tıklamadan önce gösterir. */}
            {canClose && mousePos && (
              <svg className="absolute inset-0 h-full w-full" style={{ overflow: 'visible' }}>
                <line x1={toS(mousePos).x} y1={toS(mousePos).y} x2={first.x} y2={first.y}
                  stroke={CALLOUT_COLOR} strokeOpacity={0.5} strokeWidth={1.25} strokeDasharray="3 4" />
              </svg>
            )}
          </div>
        )
      })()}

      {/* ── Point markers (DOM overlays) ─────────────────────────────────── */}
      {points.map(p => {
        const screenX = p.x * vpt.a + vpt.e
        const screenY = p.y * vpt.a + vpt.f
        const isSelected = p.id === selectedPointId
        const iconEntry = POINT_ICONS[p.icon_type] || POINT_ICONS.other
        const color = getIconColor(p.icon_type)
        return (
          <div
            key={p.id}
            data-sp-marker
            className={`absolute ${isDrawing ? 'pointer-events-none' : 'pointer-events-auto cursor-pointer'}`}
            style={{
              // Konumu left/top yerine translate3d'de tut: GPU katmanı compose
              // eder, yakınlaştırırken drop-shadow iz bırakmaz (StrategyEditor
              // ile aynı). İkon sabit ekran boyutunda, görüntü aynı yerde durur.
              left: 0, top: 0,
              transform: `translate3d(${screenX}px, ${screenY}px, 0) translate(-${ICON_SIZE / 2}px, -${PIVOT_Y}px)`,
              zIndex: isSelected ? 6 : 5,
            }}
            onClick={(e) => {
              e.stopPropagation()
              if (editingPointId !== null && editingPointId !== p.id) setEditingPointId(null)
              onSelectPoint(p.id)
            }}
            onContextMenu={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setContextMenu({ pointId: p.id, clientX: e.clientX, clientY: e.clientY })
            }}
          >
            {/* Tooltip (visible only when selected) */}
            {(() => {
              const isEditingThis = p.id === editingPointId
              const displayDesc = isEditingThis ? editingDescription : p.description
              return isSelected && (displayDesc || iconEntry.label) ? (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: '50%',
                    bottom: '100%',
                    transform: 'translateX(-50%)',
                    marginBottom: 4,
                    zIndex: 10,
                  }}
                >
                  <div
                    className="rounded-md px-2 py-1.5 shadow-lg"
                    style={{
                      background: 'rgba(0,0,0,0.80)',
                      border: `1.5px solid ${color}`,
                      minWidth: 60,
                      maxWidth: 200,
                      wordBreak: 'break-word',
                    }}
                  >
                    <div className="text-[11px] font-bold text-white" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{iconEntry.label}</div>
                    {displayDesc && (
                      <div className="text-[10px] text-white/70 mt-0.5" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{displayDesc}</div>
                    )}
                    <div className="text-[9px] text-white/40 mt-0.5">{p.created_by_name}</div>
                  </div>
                </div>
              ) : null
            })()}

            {/* Icon */}
            <div
              className="flex items-center justify-center"
              style={{
                width: ICON_SIZE,
                height: ICON_SIZE,
                filter: 'drop-shadow(0 0 3px #000) drop-shadow(1px 2px 5px #000)',
                outline: isSelected ? '2px solid #000' : 'none',
                outlineOffset: -1,
                borderRadius: 4,
              }}
            >
              {createElement(iconEntry.icon, {
                className: 'w-5 h-5',
                style: { color },
                strokeWidth: 2.5,
              })}
            </div>

            {/* Inverted triangle */}
            <svg
              width={TRI_W}
              height={TRI_H}
              viewBox={`0 0 ${TRI_W} ${TRI_H}`}
              style={{
                position: 'absolute',
                left: '50%',
                top: TRI_TOP,
                transform: 'translateX(-50%)',
                filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.7))',
                overflow: 'visible',
              }}
            >
              <polygon
                points={`0,0 ${TRI_W},0 ${TRI_W / 2},${TRI_H}`}
                fill={color}
                stroke="#000"
                strokeWidth={1.5}
              />
            </svg>

          </div>
        )
      })}

      {/* ── Arrow preview line (anchor → mouse) ─────────────────────────── */}
      {tool === 'angle_arrow' && drawingAnchor && mousePos && (
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 7, width: '100%', height: '100%' }}
        >
          <line
            x1={drawingAnchor.x * vpt.a + vpt.e}
            y1={drawingAnchor.y * vpt.a + vpt.f}
            x2={mousePos.x * vpt.a + vpt.e}
            y2={mousePos.y * vpt.a + vpt.f}
            stroke={anchorColor}
            strokeWidth={Math.max(ANGLE_MIN_PX, ANGLE_STROKE * vpt.a)}
            strokeDasharray="8 5"
            strokeLinecap="round"
            opacity={1}
          />
        </svg>
      )}

      {/* ── Polygon preview lines (anchor → points → mouse) ──────────── */}
      {tool === 'angle_polygon' && drawingAnchor && (drawingAnglePoints.length > 0 || mousePos) && (
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 7, width: '100%', height: '100%' }}
        >
          {/* Lines between drawing points */}
          {drawingAnglePoints.map((pt, i) => {
            const prev = i === 0
              ? { x: drawingAnchor.x, y: drawingAnchor.y }
              : drawingAnglePoints[i - 1]
            return (
              <line
                key={`seg-${i}`}
                x1={prev.x * vpt.a + vpt.e}
                y1={prev.y * vpt.a + vpt.f}
                x2={pt.x * vpt.a + vpt.e}
                y2={pt.y * vpt.a + vpt.f}
                stroke={anchorColor}
                strokeWidth={Math.max(ANGLE_MIN_PX, ANGLE_STROKE * vpt.a)}
                strokeDasharray="8 5"
                strokeLinecap="round"
                opacity={1}
              />
            )
          })}
          {/* Preview line from last point to mouse */}
          {mousePos && drawingAnglePoints.length > 0 && (
            <line
              x1={drawingAnglePoints[drawingAnglePoints.length - 1].x * vpt.a + vpt.e}
              y1={drawingAnglePoints[drawingAnglePoints.length - 1].y * vpt.a + vpt.f}
              x2={mousePos.x * vpt.a + vpt.e}
              y2={mousePos.y * vpt.a + vpt.f}
              stroke={anchorColor}
              strokeWidth={Math.max(ANGLE_MIN_PX, ANGLE_STROKE * vpt.a)}
              strokeDasharray="8 5"
              strokeLinecap="round"
              opacity={1}
            />
          )}
          {/* Preview line from mouse back to anchor (closing line) */}
          {mousePos && drawingAnglePoints.length >= 2 && (
            <line
              x1={mousePos.x * vpt.a + vpt.e}
              y1={mousePos.y * vpt.a + vpt.f}
              x2={drawingAnchor.x * vpt.a + vpt.e}
              y2={drawingAnchor.y * vpt.a + vpt.f}
              stroke={anchorColor}
              strokeWidth={Math.max(ANGLE_MIN_PX * 0.8, ANGLE_STROKE * vpt.a * 0.8)}
              strokeDasharray="5 5"
              strokeLinecap="round"
              opacity={0.6}
            />
          )}
        </svg>
      )}

      {/* ── Polygon starting handle (always visible while drawing) ───────── */}
      {tool === 'angle_polygon' && drawingAnchor && (() => {
        const ax = drawingAnchor.x * vpt.a + vpt.e
        const ay = drawingAnchor.y * vpt.a + vpt.f
        const canClose = drawingAnglePoints.length >= 2
        return (
          <div
            className="absolute pointer-events-none"
            style={{
              left: ax,
              top: ay,
              transform: 'translate(-50%, -50%)',
              zIndex: 8,
            }}
          >
            <div
              style={{
                width: canClose ? 14 : 10,
                height: canClose ? 14 : 10,
                borderRadius: '50%',
                border: `2.5px solid ${anchorColor}`,
                background: canClose ? 'rgba(255,255,255,0.95)' : '#fff',
                boxShadow: `0 0 8px ${anchorColor}${canClose ? '80' : '40'}`,
                transition: 'width 120ms, height 120ms',
              }}
            />
          </div>
        )
      })()}

      {/* ── Ping animations ──────────────────────────────────────────────── */}
      {pings.map(ping => {
        const screenX = ping.x * vpt.a + vpt.e
        const screenY = ping.y * vpt.a + vpt.f
        return (
          <div
            key={ping.id}
            className="absolute pointer-events-none animate-ping-fadeout"
            style={{ left: screenX, top: screenY, transform: 'translate(-50%, -50%)' }}
          >
            <div className="h-10 w-10 rounded-full border-2 border-red-500 animate-ping opacity-80" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-2 w-2 rounded-full bg-red-500" />
            </div>
          </div>
        )
      })}

      {/* ── Drawing angle preview dots ────────────────────────────────────── */}
      {drawingAnglePoints.map((pt, i) => {
        const screenX = pt.x * vpt.a + vpt.e
        const screenY = pt.y * vpt.a + vpt.f
        return (
          <div
            key={`draw-${i}`}
            className="absolute pointer-events-none rounded-full"
            style={{
              left: screenX,
              top: screenY,
              transform: 'translate(-50%, -50%)',
              width: 8,
              height: 8,
              background: anchorColor,
              border: '1px solid #fff',
              boxShadow: '0 0 4px rgba(0,0,0,0.5)',
              zIndex: 8,
            }}
          />
        )
      })}

      {/* ── Viewing angles (arrows + polygons, DOM SVG, selected SP only) ── */}
      {(() => {
        const items: { angle: ViewingAngle; idx: number; point: StrategicPointData }[] = []
        for (const point of points) {
          if (point.id !== selectedPointId) continue
          for (let i = 0; i < (point.viewing_angles || []).length; i++) {
            const angle = point.viewing_angles[i]
            if (angle.points.length >= 2) items.push({ angle, idx: i, point })
          }
        }
        if (!items.length) return null
        const polygons = items.filter(({ angle }) => angle.type === 'polygon')
        const arrows   = items.filter(({ angle }) => angle.type === 'arrow')
        return (
          <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 3, width: '100%', height: '100%' }}>
            <defs>
              {polygons.map(({ angle, idx, point }) => {
                const color = angle.color || '#ef4444'
                const sp = angle.points[0]
                const spSx = sp.x * vpt.a + vpt.e
                const spSy = sp.y * vpt.a + vpt.f
                const maxDist = Math.max(1, ...angle.points.slice(1).map(p =>
                  Math.hypot(p.x * vpt.a + vpt.e - spSx, p.y * vpt.a + vpt.f - spSy)
                ))
                return (
                  <radialGradient
                    key={`grad-${point.id}-${idx}`}
                    id={`grad-${point.id}-${idx}`}
                    cx={spSx} cy={spSy} r={maxDist}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0%"   stopColor={color} stopOpacity="0.9" />
                    <stop offset="30%"  stopColor={color} stopOpacity="0.7" />
                    <stop offset="65%"  stopColor={color} stopOpacity="0.25" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                  </radialGradient>
                )
              })}
            </defs>

            {/* Polygons — radial gradient fill from SP outward */}
            {polygons.map(({ angle, idx, point }) => {
              const sw = Math.max(ANGLE_MIN_PX, ANGLE_STROKE * vpt.a)
              const pts = angle.points.map(p => `${p.x * vpt.a + vpt.e},${p.y * vpt.a + vpt.f}`).join(' ')
              return (
                <polygon
                  key={`poly-${point.id}-${idx}`}
                  points={pts}
                  fill={`url(#grad-${point.id}-${idx})`}
                  stroke={`url(#grad-${point.id}-${idx})`}
                  strokeWidth={sw}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              )
            })}

            {/* Arrows — V-shape lines, no filled head */}
            {arrows.map(({ angle, idx, point }) => {
              const start = angle.points[0]
              const end   = angle.points[angle.points.length - 1]
              const x1 = start.x * vpt.a + vpt.e
              const y1 = start.y * vpt.a + vpt.f
              const x2 = end.x * vpt.a + vpt.e
              const y2 = end.y * vpt.a + vpt.f
              const color = angle.color || '#ef4444'
              const sw  = Math.max(ANGLE_MIN_PX, ANGLE_STROKE * vpt.a)
              const dir = Math.atan2(y2 - y1, x2 - x1)
              const h   = Math.max(5 * DRAW_SCALE * vpt.a, sw * 5)
              return (
                <g key={`arr-${point.id}-${idx}`}>
                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={sw} strokeLinecap="round" />
                  <line x1={x2} y1={y2} x2={x2 - h * Math.cos(dir - Math.PI / 6)} y2={y2 - h * Math.sin(dir - Math.PI / 6)} stroke={color} strokeWidth={sw} strokeLinecap="round" />
                  <line x1={x2} y1={y2} x2={x2 - h * Math.cos(dir + Math.PI / 6)} y2={y2 - h * Math.sin(dir + Math.PI / 6)} stroke={color} strokeWidth={sw} strokeLinecap="round" />
                </g>
              )
            })}
          </svg>
        )
      })()}

      {/* ── Left toolbar ─────────────────────────────────────────────────── */}
      {/* Mobilde salt görüntüleme: araç rayı düşer (StrategyEditor ile aynı). */}
      <div className={`absolute left-3 top-1/2 hidden -translate-y-1/2 flex-col gap-1.5 p-1.5 md:flex ${editorSurface}`}>
        {toolBtn('select', MousePointer2, T.strategicPoints.panel.selectTitle)}
        {toolBtn('point', MapPin, T.strategicPoints.panel.pointTitle)}
        {toolBtn('callout', MapPinned, T.callouts.tool)}
        {toolBtn('ping', Goal, T.strategy.tools.ping)}

        {/* Harita zemini: taktiksel (gri, normalize) ↔ uydu (ham renk) */}
        <div className="mx-1 h-px bg-white/10" />
        <button
          onClick={toggleMapView}
          className={editorIconButton(mapView === 'tactical')}
          title={mapView === 'tactical' ? T.strategy.mapView.tacticalHint : T.strategy.mapView.satelliteHint}
          aria-label={T.strategy.mapView.label}
        >
          {mapView === 'tactical' ? <Crosshair className="h-4 w-4" /> : <Satellite className="h-4 w-4" />}
        </button>
      </div>


      {/* ── SP placement preview (follows mouse when tool=point & icon selected) */}
      {tool === 'point' && selectedIconType !== '' && markerMousePos && (() => {
        const previewIcon = POINT_ICONS[selectedIconType] || POINT_ICONS.other
        const previewColor = previewIcon.color
        const sx = markerMousePos.x * vpt.a + vpt.e
        const sy = markerMousePos.y * vpt.a + vpt.f
        return (
          <div
            className="absolute pointer-events-none"
            style={{
              left: sx,
              top: sy,
              transform: `translate(-${ICON_SIZE / 2}px, -${PIVOT_Y}px)`,
              zIndex: 8,
              opacity: 0.6,
            }}
          >
            <div
              className="flex items-center justify-center"
              style={{ width: ICON_SIZE, height: ICON_SIZE }}
            >
              {createElement(previewIcon.icon, {
                className: 'w-5 h-5',
                style: { color: previewColor },
                strokeWidth: 2.5,
              })}
            </div>
            <svg
              width={TRI_W}
              height={TRI_H}
              viewBox={`0 0 ${TRI_W} ${TRI_H}`}
              style={{
                position: 'absolute',
                left: '50%',
                top: TRI_TOP,
                transform: 'translateX(-50%)',
                overflow: 'visible',
              }}
            >
              <polygon
                points={`0,0 ${TRI_W},0 ${TRI_W / 2},${TRI_H}`}
                fill={previewColor}
                stroke="#000"
                strokeWidth={1.5}
              />
            </svg>
          </div>
        )
      })()}

      {/* ── Right floating panel (icon picker / point view / point edit) ────── */}
      {(() => {
        const selectedPoint = points.find(p => p.id === selectedPointId) ?? null
        const editingPoint = editingPointId ? points.find(p => p.id === editingPointId) ?? null : null
        const isEditing = !!editingPoint
        const isViewing = !!selectedPoint && !isEditing
        const panelLabel = pendingCallout
          ? T.callouts.newTitle
          : selectedCallout || tool === 'callout'
            ? T.callouts.title
            : isEditing
          ? pointIconLabel(editingPoint?.icon_type ?? 'other', T)
          : isViewing
            ? pointIconLabel(selectedPoint?.icon_type ?? 'other', T)
            : tool === 'point' ? T.strategicPoints.panel.pointTitle : T.strategicPoints.panel.selectTitle
        const currentPoint = editingPoint || selectedPoint
        const hasMedia = (currentPoint?.media || []).length > 0
        const panelWidth = rightPanelOpen ? (hasMedia ? 520 : 224) : 40

        return (
          <div
            className={`absolute right-3 top-1/2 z-10 hidden -translate-y-1/2 flex-col overflow-hidden transition-[width] duration-300 md:flex ${editorSurface}`}
            data-ui-panel
            style={{ width: panelWidth, maxHeight: 'calc(100% - 24px)' }}
          >
            {/* Header row — always visible */}
            <div className="flex h-10 shrink-0 items-center gap-2 border-b border-white/10 px-1.5">
              <button
                onClick={() => setRightPanelOpen(v => !v)}
                className={editorIconButton(false, 'h-7 w-7')}
                title={rightPanelOpen ? T.strategicPoints.panel.collapse : T.strategicPoints.panel.expand}
              >
                <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${rightPanelOpen ? '' : 'rotate-180'}`} />
              </button>
              {rightPanelOpen && (
                <span className="flex-1 truncate font-display text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground/70">
                  {panelLabel}
                </span>
              )}
              {rightPanelOpen && currentPoint && (isOperator(user) || user?.id === currentPoint.created_by) && (
                <button
                  onClick={() => isEditing ? setEditingPointId(null) : setEditingPointId(currentPoint.id)}
                  className={`h-7 w-7 rounded flex items-center justify-center transition-colors shrink-0 ${
                    isEditing
                      ? 'bg-primary/20 text-primary'
                      : 'text-muted-foreground hover:bg-white/10 hover:text-foreground'
                  }`}
                  title={isEditing ? T.strategicPoints.panel.exitEdit : T.strategicPoints.panel.editPoint}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Panel content */}
            {rightPanelOpen && (
              pendingCallout ? (
                /* ── Yeni mevki: çokgen kapandı, ad bekleniyor ─────────── */
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  <div>
                    <label className={`mb-1.5 block uppercase ${editorLabel}`}>{T.callouts.name}</label>
                    <input
                      autoFocus
                      value={pendingName}
                      maxLength={CALLOUT_NAME_MAX}
                      onChange={e => setPendingName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); savePendingCallout() } }}
                      placeholder={T.callouts.namePlaceholder}
                      className={editorInput}
                    />
                    <p className="mt-1.5 text-[10px] leading-relaxed text-muted-foreground/50">{T.callouts.nameHint}</p>
                  </div>
                  {calloutError && <p className="text-[10px] text-red-400">{calloutError}</p>}
                  <div className="flex gap-1.5">
                    <button onClick={cancelPendingCallout} className={editorChoiceButton(false)}>
                      {T.callouts.cancel}
                    </button>
                    <button
                      onClick={savePendingCallout}
                      disabled={!pendingName.trim()}
                      className={`${editorChoiceButton(true)} disabled:cursor-default disabled:opacity-40`}
                    >
                      {T.callouts.save}
                    </button>
                  </div>
                </div>
              ) : selectedCallout ? (
                /* ── Seçili mevki ─────────────────────────────────────── */
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  <div>
                    <label className={`mb-1.5 block uppercase ${editorLabel}`}>{T.callouts.name}</label>
                    {selectedCallout.can_edit ? (
                      <input
                        value={calloutNameDraft}
                        maxLength={CALLOUT_NAME_MAX}
                        onChange={e => setCalloutNameDraft(e.target.value)}
                        onBlur={saveCalloutName}
                        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                        className={editorInput}
                      />
                    ) : (
                      // Mevki adı veridir, büyütülmez.
                      <p className="text-sm font-bold" style={{ color: CALLOUT_COLOR }}>{selectedCallout.name}</p>
                    )}
                  </div>
                  <p className="text-[10px] leading-relaxed text-muted-foreground/50">
                    {selectedCallout.can_edit ? T.callouts.editHint : T.callouts.readOnly}
                  </p>
                  <p className="text-[10px] tabular-nums text-muted-foreground/60">
                    {T.callouts.corners.replace('{n}', String(selectedCallout.points.length))}
                  </p>
                  {calloutError && <p className="text-[10px] text-red-400">{calloutError}</p>}

                  <div className="pt-2 border-t border-white/10 flex items-center gap-2">
                    {selectedCallout.created_by_avatar ? (
                      <img
                        src={selectedCallout.created_by_avatar}
                        alt={selectedCallout.created_by_name}
                        className="w-6 h-6 rounded-full object-cover ring-1 ring-white/10 shrink-0"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                        {(selectedCallout.created_by_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-foreground/70 truncate">{selectedCallout.created_by_name}</p>
                      <p className="text-[10px] text-muted-foreground/60">
                        {new Date(selectedCallout.created_at * 1000).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </p>
                    </div>
                    {selectedCallout.can_edit && (
                      <button
                        onClick={() => { if (confirm(T.callouts.confirmDelete)) onDeleteCallout(selectedCallout.id) }}
                        className="shrink-0 p-1 rounded text-muted-foreground/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title={T.callouts.delete}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ) : tool === 'callout' ? (
                <div className="flex-1 overflow-y-auto p-3">
                  <p className="text-[10px] leading-relaxed text-muted-foreground/60">{T.callouts.toolHint}</p>
                </div>
              ) : isEditing && editingPoint ? (
                /* ── EDIT mode (after placing or right-click Edit) ─────── */
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {/* Icon type */}
                  <div>
                    <label className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 block mb-1.5">{T.strategicPoints.panel.icon}</label>
                    <div className="grid grid-cols-4 gap-1">
                      {Object.entries(POINT_ICONS).map(([key, { icon, color }]) => (
                        <button
                          key={key}
                          onClick={() => {
                            fetch(`/api/strategic-points/${editingPoint.id}`, {
                              method: 'PUT',
                              credentials: 'include',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ icon_type: key }),
                            }).then(res => {
                              if (res.ok) onUpdatePoint(editingPoint.id, { icon_type: key })
                            })
                          }}
                          className={`flex flex-col items-center gap-0.5 p-1.5 rounded-lg transition-colors ${
                            editingPoint.icon_type === key
                              ? 'bg-primary/20 ring-1 ring-primary'
                              : 'hover:bg-white/10'
                          }`}
                          title={pointIconLabel(key, T)}
                        >
                          {createElement(icon, { className: 'h-3.5 w-3.5', style: { color } })}
                          <span className="text-[7px] font-semibold leading-none" style={{ color }}>
                            {pointIconLabel(key, T)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 block mb-1">{T.strategicPoints.panel.description}</label>
                    <textarea
                      value={editingDescription}
                      onChange={e => setEditingDescription(e.target.value)}
                      onBlur={() => {
                        fetch(`/api/strategic-points/${editingPoint.id}`, {
                          method: 'PUT',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ description: editingDescription }),
                        }).then(res => { if (res.ok) onUpdatePoint(editingPoint.id, { description: editingDescription }) })
                      }}
                      className="w-full rounded px-2 py-1.5 text-sm bg-sidebar border border-white/10 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none"
                      rows={2}
                      placeholder={T.strategicPoints.panel.notesPlaceholder}
                    />
                  </div>

                  {/* Viewing angles — draw tools */}
                  <div>
                    <label className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 block mb-1.5">{T.strategicPoints.panel.viewingAngles}</label>
                    <div className="flex gap-1 mb-1.5">
                      <button
                        onClick={() => {
                          if (tool === 'angle_arrow') {
                            drawingAngleRef.current = null
                            setDrawingAnglePoints([])
                            setMousePos(null)
                            setTool('select')
                          } else {
                            drawingAngleRef.current = { pointId: editingPoint.id, type: 'arrow' }
                            setDrawingAnglePoints([])
                            onSelectPoint(editingPoint.id)
                            setTool('angle_arrow')
                          }
                        }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-[10px] font-semibold transition-colors ${
                          tool === 'angle_arrow'
                            ? 'bg-primary/20 ring-1 ring-primary text-primary'
                            : 'bg-white/5 hover:bg-white/10 text-muted-foreground'
                        }`}
                        title={T.strategicPoints.draw.arrowFrom}
                      >
                        <MoveUpRight className="h-3 w-3" />
                        {T.strategicPoints.draw.arrow}
                      </button>
                      <button
                        onClick={() => {
                          if (tool === 'angle_polygon') {
                            drawingAngleRef.current = null
                            setDrawingAnglePoints([])
                            setMousePos(null)
                            setTool('select')
                          } else {
                            drawingAngleRef.current = { pointId: editingPoint.id, type: 'polygon' }
                            setDrawingAnglePoints([])
                            onSelectPoint(editingPoint.id)
                            setTool('angle_polygon')
                          }
                        }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-[10px] font-semibold transition-colors ${
                          tool === 'angle_polygon'
                            ? 'bg-primary/20 ring-1 ring-primary text-primary'
                            : 'bg-white/5 hover:bg-white/10 text-muted-foreground'
                        }`}
                        title={T.strategicPoints.draw.polygonFrom}
                      >
                        <LandPlot className="h-3 w-3" />
                        {T.strategicPoints.draw.polygon}
                      </button>
                    </div>
                    {(editingPoint.viewing_angles || []).length > 0 && (
                      <div className="space-y-0.5">
                        {(editingPoint.viewing_angles || []).map((angle, idx) => {
                          const color = angle.color || '#ef4444'
                          return (
                            <div key={idx} className="flex items-center gap-2 px-2 py-1 rounded bg-white/5">
                              {angle.type === 'arrow'
                                ? <MoveUpRight className="h-3 w-3 shrink-0" style={{ color }} />
                                : <LandPlot className="h-3 w-3 shrink-0" style={{ color }} />
                              }
                              <span className="text-[9px] text-foreground/50 flex-1">
                                {angle.type === 'arrow' ? T.strategicPoints.draw.arrow : T.strategicPoints.draw.polygon}
                              </span>
                              <button
                                onClick={() => deleteViewingAngle(editingPoint.id, idx)}
                                className="text-muted-foreground/25 hover:text-red-400 transition-colors"
                                title={T.strategicPoints.panel.delete}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {/* Media */}
                  <div>
                    <label className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 block mb-1.5">{T.strategicPoints.panel.media}</label>
                    <SPMediaUploader
                      point={editingPoint}
                      uploading={uploading}
                      setUploading={setUploading}
                      onUpdatePoint={onUpdatePoint}
                    />
                  </div>

                  {/* Created by (compact) + delete */}
                  <div className="pt-2 border-t border-white/10 flex items-center gap-2">
                    {editingPoint.created_by_avatar ? (
                      <img
                        src={editingPoint.created_by_avatar}
                        alt={editingPoint.created_by_name}
                        className="w-5 h-5 rounded-full object-cover ring-1 ring-white/10 shrink-0"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] font-bold text-white shrink-0">
                        {(editingPoint.created_by_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-[9px] font-semibold text-foreground/60 truncate">{editingPoint.created_by_name}</p>
                    </div>
                    <button
                      onClick={() => {
                        if (confirm(T.strategicPoints.panel.confirmDelete)) {
                          deletePoint(editingPoint.id)
                          setEditingPointId(null)
                        }
                      }}
                      className="ml-auto shrink-0 p-1 rounded text-muted-foreground/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title={T.strategicPoints.panel.deletePoint}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ) : isViewing && selectedPoint ? (
                /* ── VIEW mode (left-click on point) — media + created by ── */
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {/* Icon + type name */}
                  <div className="flex items-center gap-2">
                    {createElement((POINT_ICONS[selectedPoint.icon_type] || POINT_ICONS.other).icon, {
                      className: 'h-4 w-4',
                      style: { color: getIconColor(selectedPoint.icon_type) },
                    })}
                    <span className="text-sm font-bold text-foreground">
                      {pointIconLabel(selectedPoint.icon_type, T)}
                    </span>
                  </div>
                  {selectedPoint.description && (
                    <div className="text-[11px] text-muted-foreground leading-relaxed">{selectedPoint.description}</div>
                  )}

                  {/* Media gallery */}
                  {(selectedPoint.media || []).length > 0 ? (
                    <div>
                      <label className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 block mb-1.5">{T.strategicPoints.panel.media}</label>
                      <div className="space-y-1.5">
                        {(selectedPoint.media || []).map(m => (
                          <div key={m.id} className="rounded border border-white/10 bg-white/5 overflow-hidden">
                            <MediaDisplay url={m.url} mediaType={m.media_type} maxH={96} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1.5 py-3 rounded-lg border border-dashed border-white/15 bg-white/[0.02]">
                      <ImageIcon className="h-5 w-5 text-muted-foreground/25" />
                      <span className="text-[10px] font-medium text-muted-foreground/50">{T.strategicPoints.panel.noMedia}</span>
                    </div>
                  )}

                  {/* Created by + Edit */}
                  <div className="pt-2 border-t border-white/10 flex items-center gap-2">
                    {selectedPoint.created_by_avatar ? (
                      <img
                        src={selectedPoint.created_by_avatar}
                        alt={selectedPoint.created_by_name}
                        className="w-7 h-7 rounded-full object-cover ring-1 ring-white/10 shrink-0"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-[11px] font-bold text-white shrink-0">
                        {(selectedPoint.created_by_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-foreground/70 truncate">{selectedPoint.created_by_name}</p>
                      {selectedPoint.created_at ? (
                        <p className="text-[10px] text-muted-foreground/60">
                          {new Date(selectedPoint.created_at * 1000).toLocaleDateString('tr-TR', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : tool === 'point' ? (
                /* ── Icon picker (when no point selected) ──────────────── */
                <div className="flex-1 overflow-y-auto p-3">
                  <p className="text-[10px] font-medium text-foreground/40 tracking-wider mb-2">{T.strategicPoints.panel.selectIcon}</p>
                  <div className="grid grid-cols-4 gap-1">
                    {Object.entries(POINT_ICONS).map(([key, { icon, color }]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedIconType(key)}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                          selectedIconType === key
                            ? 'bg-primary/20 ring-1 ring-primary'
                            : 'hover:bg-white/10'
                        }`}
                        title={pointIconLabel(key, T)}
                      >
                        {createElement(icon, { className: 'h-4 w-4', style: { color } })}
                        <span className="text-[8px] font-semibold leading-none" style={{ color }}>
                          {pointIconLabel(key, T)}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="text-[9px] text-muted-foreground/40 mt-2 leading-relaxed">
                    {T.strategicPoints.panel.selectIconHint}
                  </p>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto p-3">
                  <p className="text-[10px] text-muted-foreground/40">{T.strategicPoints.panel.idleHint}</p>
                </div>
              )
            )}
          </div>
        )
      })()}

      {/* ── Drawing mode indicator ────────────────────────────────────────── */}
      {(tool === 'angle_polygon' || tool === 'angle_arrow' || tool === 'callout') && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 rounded-lg px-3 py-1.5 text-xs font-semibold pointer-events-none">
          {tool === 'callout'
            ? T.callouts.drawHint
            : tool === 'angle_polygon'
              ? T.strategicPoints.draw.polygonHint
              : T.strategicPoints.draw.arrowHint}
        </div>
      )}

      {/* ── Right-click context menu ──────────────────────────────────────── */}
      {contextMenu && (() => {
        const cont = containerRef.current
        const rect = cont ? cont.getBoundingClientRect() : { left: 0, top: 0 }
        const ctxPoint = points.find(p => p.id === contextMenu.pointId)
        const canEditCtx = isOperator(user) || user?.id === ctxPoint?.created_by
        if (!canEditCtx) return null
        return (
          <>
            <div
              className="fixed inset-0 z-[99]"
              onClick={() => setContextMenu(null)}
              onContextMenu={(e) => { e.preventDefault(); setContextMenu(null) }}
            />
            <div
              className={`absolute z-[100] min-w-[150px] overflow-hidden py-1 ${editorMenu}`}
              style={{
                left: contextMenu.clientX - rect.left,
                top: contextMenu.clientY - rect.top,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  setEditingPointId(contextMenu.pointId)
                  onSelectPoint(contextMenu.pointId)
                  setContextMenu(null)
                }}
                className={editorMenuItem}
              >
                <MousePointer2 className="h-3.5 w-3.5" />
                {T.strategicPoints.panel.editPoint}
              </button>
              <button
                onClick={() => {
                  deletePoint(contextMenu.pointId)
                  setContextMenu(null)
                }}
                className={editorMenuItemDanger}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {T.strategicPoints.panel.deletePoint}
              </button>
            </div>
          </>
        )
      })()}
    </div>
  )
}

// ── Media uploader sub-component ────────────────────────────────────────────
function SPMediaUploader({
  point,
  uploading,
  setUploading,
  onUpdatePoint,
}: {
  point: StrategicPointData
  uploading: boolean
  setUploading: (v: boolean) => void
  onUpdatePoint: (id: number, updates: Partial<StrategicPointData>) => void
}) {
  const { T } = useLanguage()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkError, setLinkError] = useState('')
  const [uploadError, setUploadError] = useState('')

  const MAX_MB = 25

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError('')

    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadError(`Dosya çok büyük. Maksimum ${MAX_MB} MB.`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`/api/strategic-points/${point.id}/media`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      if (res.ok) {
        const newMedia: MediaItem = await res.json()
        onUpdatePoint(point.id, { media: [...(point.media || []), newMedia] })
      } else {
        const body = await res.json().catch(() => ({}))
        setUploadError(body.detail || `Yükleme başarısız (HTTP ${res.status})`)
      }
    } catch (err) {
      setUploadError('Yükleme başarısız, ağ hatası')
    }
    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleAddLink = async () => {
    const url = linkUrl.trim()
    if (!url) return
    setLinkError('')
    const lower = url.toLowerCase()
    const isVideo = lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.includes('video')
    const mediaType = isVideo ? 'video' : 'image'
    try {
      const res = await fetch(`/api/strategic-points/${point.id}/media/link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, media_type: mediaType }),
      })
      if (res.ok) {
        const newMedia: MediaItem = await res.json()
        onUpdatePoint(point.id, { media: [...(point.media || []), newMedia] })
        setLinkUrl('')
      } else {
        setLinkError(T.strategicPoints.media.linkFailed)
      }
    } catch {
      setLinkError(T.strategicPoints.media.linkFailed)
    }
  }

  const handleDeleteMedia = async (mediaId: number) => {
    try {
      const res = await fetch(`/api/strategic-points/${point.id}/media/${mediaId}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (res.ok) {
        onUpdatePoint(point.id, { media: (point.media || []).filter(m => m.id !== mediaId) })
      }
    } catch { /* ignore */ }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif,video/mp4,video/webm"
        onChange={handleFileSelect}
        className="hidden"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 rounded px-2 py-1.5 text-[10px] font-semibold
          border border-dashed border-white/15 text-muted-foreground/50
          hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40"
      >
        <Upload className="h-3 w-3" />
        {uploading ? T.strategicPoints.media.uploading : T.strategicPoints.media.upload}
      </button>
      {uploadError && <p className="text-[9px] text-red-400 mt-0.5">{uploadError}</p>}

      {/* Link input */}
      <div className="flex gap-1 mt-1.5">
        <input
          type="url"
          value={linkUrl}
          onChange={e => { setLinkUrl(e.target.value); setLinkError('') }}
          onKeyDown={e => { if (e.key === 'Enter') handleAddLink() }}
          placeholder={T.strategicPoints.media.linkPlaceholder}
          className="flex-1 min-w-0 rounded px-2 py-1 text-[10px] bg-sidebar border border-white/10 text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-primary/40"
        />
        <button
          onClick={handleAddLink}
          disabled={!linkUrl.trim()}
          className="shrink-0 px-2 py-1 rounded bg-primary/20 hover:bg-primary/30 text-primary text-[10px] font-semibold transition-colors disabled:opacity-30"
        >
          {T.strategicPoints.media.addLink}
        </button>
      </div>
      {linkError && <p className="text-[9px] text-red-400 mt-0.5">{linkError}</p>}

      {(point.media || []).length > 0 && (
        <div className="mt-2 space-y-1.5">
          {(point.media || []).map(m => (
            <div key={m.id} className="rounded border border-white/10 bg-white/5 overflow-hidden">
              <MediaDisplay url={m.url} mediaType={m.media_type} maxH={56} />
              <div className="flex items-center gap-1.5 px-1.5 py-1">
                {m.media_type === 'image' ? (
                  <ImageIcon className="h-2.5 w-2.5 text-muted-foreground/50 shrink-0" />
                ) : (
                  <Video className="h-2.5 w-2.5 text-muted-foreground/50 shrink-0" />
                )}
                <span className="text-[9px] text-muted-foreground truncate flex-1">{m.filename}</span>
                <button
                  onClick={() => handleDeleteMedia(m.id)}
                  className="text-muted-foreground/30 hover:text-red-400 transition-colors shrink-0"
                  title={T.strategicPoints.media.deleteMedia}
                >
                  <Trash2 className="h-2.5 w-2.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ── Lightbox portal ────────────────────────────────────────────────────────
function Lightbox({ url, mediaType, onClose }: { url: string; mediaType: string; onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm cursor-zoom-out"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      {mediaType === 'youtube' ? (
        <div
          className="rounded-xl overflow-hidden shadow-2xl"
          style={{ width: 'min(85vw, 1280px)', aspectRatio: '16/9' }}
          onClick={e => e.stopPropagation()}
        >
          <iframe
            src={`https://www.youtube.com/embed/${url}?autoplay=1`}
            className="w-full h-full"
            style={{ border: 'none' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : mediaType === 'video' ? (
        <video
          src={url}
          controls
          autoPlay
          className="rounded-xl shadow-2xl"
          style={{ maxWidth: 'min(85vw, 1280px)', maxHeight: '85vh' }}
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <img
          src={url}
          alt=""
          className="rounded-xl shadow-2xl object-contain"
          style={{ maxWidth: 'min(85vw, 1280px)', maxHeight: '85vh' }}
          onClick={e => e.stopPropagation()}
        />
      )}
    </div>,
    document.body,
  )
}

// ── Media display — handles both image and video with error fallback ─────────
function MediaDisplay({ url, mediaType, maxH = 28 }: { url: string; mediaType: string; maxH?: number }) {
  const { T } = useLanguage()
  const [broken, setBroken] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const maxHeight = maxH * 4

  // YouTube — thumbnail with play button, click opens lightbox
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
  if (ytMatch) {
    const ytId = ytMatch[1]
    return (
      <>
        {lightboxOpen && <Lightbox url={ytId} mediaType="youtube" onClose={() => setLightboxOpen(false)} />}
        <div
          className="relative w-full cursor-pointer group"
          style={{ aspectRatio: '16/9' }}
          onClick={() => setLightboxOpen(true)}
        >
          <img
            src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
            alt=""
            className="w-full h-full object-cover block"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-red-600/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <svg className="w-6 h-6 text-white translate-x-[2px]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (broken) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-5 bg-white/[0.03]" style={{ minHeight: 80 }}>
        <ImageIcon className="h-6 w-6 text-muted-foreground/25" />
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="text-[9px] text-primary/60 hover:text-primary hover:underline">
          Open in new tab ↗
        </a>
      </div>
    )
  }

  return (
    <>
      {lightboxOpen && <Lightbox url={url} mediaType={mediaType} onClose={() => setLightboxOpen(false)} />}
      {mediaType === 'video' ? (
        <div className="relative group/vid">
          <video
            src={url}
            controls
            className="block w-auto max-w-full h-auto mx-auto"
            style={{ maxHeight }}
            onError={() => setBroken(true)}
          />
          <button
            onClick={() => setLightboxOpen(true)}
            className="absolute top-1.5 right-1.5 h-6 w-6 rounded bg-black/60 hover:bg-black/80 flex items-center justify-center opacity-0 group-hover/vid:opacity-100 transition-opacity"
            title={T.strategicPoints.media.expand}
          >
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
            </svg>
          </button>
        </div>
      ) : (
        <img
          src={url}
          alt=""
          className="block w-auto max-w-full h-auto mx-auto cursor-zoom-in"
          style={{ maxHeight }}
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          onClick={() => setLightboxOpen(true)}
        />
      )}
    </>
  )
}

